'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, CheckCircle2, Send, AlertCircle, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { customRequestsApi, publicApi } from '@/lib/api';
import { cn } from '@/lib/utils';

type Step = 'idle' | 'submitting' | 'success' | 'error';

interface Region { id: string; name: string; }
interface City { id: string; name: string; regionId: string; }
interface District { id: string; name: string; cityId: string; }

/* ── Generic autocomplete field ─────────────────────────────────────────────── */

interface AutocompleteProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (val: string) => void;
  options: string[];
  disabled?: boolean;
}

function Autocomplete({ label, placeholder, value, onChange, options, disabled }: AutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQuery(value); }, [value]);

  const filtered = query.trim()
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase())).slice(0, 40)
    : options.slice(0, 40);

  const select = (opt: string) => {
    setQuery(opt);
    onChange(opt);
    setOpen(false);
  };

  const clear = () => {
    setQuery('');
    onChange('');
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        if (query && !options.includes(query)) onChange(query);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [query, options, onChange]);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-ink">{label}</label>
      <div ref={wrapRef} className="relative">
        <input
          type="text"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          className={cn(
            'w-full h-12 rounded-xl bg-surface text-ink text-[15px] pl-4 pr-9',
            'border border-border-subtle hover:border-border',
            'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25',
            'transition-colors duration-250 placeholder:text-ink-muted/70',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          autoComplete="off"
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 pointer-events-none">
          {query ? (
            <button
              type="button"
              className="pointer-events-auto text-ink-muted hover:text-ink"
              onMouseDown={(e) => { e.preventDefault(); clear(); }}
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-ink-muted" />
          )}
        </span>

        {open && filtered.length > 0 && (
          <ul className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-xl border border-border bg-background shadow-lg py-1">
            {filtered.map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); select(opt); }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm hover:bg-accent/10 transition-colors',
                    opt === value && 'bg-accent/10 font-medium text-accent',
                  )}
                >
                  {opt}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ── Main form ───────────────────────────────────────────────────────────────── */

export function CustomRequestForm() {
  const [step, setStep] = useState<Step>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const [intent, setIntent] = useState<'buy' | 'rent'>('buy');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [address, setAddress] = useState('');
  const [message, setMessage] = useState('');

  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);

  useEffect(() => {
    publicApi.getRegions().then((d: any) => setRegions(Array.isArray(d) ? d : [])).catch(() => { });
    publicApi.getCities().then((d: any) => setCities(Array.isArray(d) ? d : [])).catch(() => { });
  }, []);

  // Fetch districts whenever the selected city changes
  useEffect(() => {
    setDistricts([]);
    setDistrict('');
    if (!city) return;
    const found = cities.find((c) => c.name === city);
    if (!found) return;
    publicApi.getDistricts(found.id)
      .then((d: any) => setDistricts(Array.isArray(d) ? d : []))
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  const regionNames = regions.map((r) => r.name);

  const cityNames = (() => {
    if (!region) return cities.map((c) => c.name);
    const found = regions.find((r) => r.name === region);
    return found ? cities.filter((c) => c.regionId === found.id).map((c) => c.name) : cities.map((c) => c.name);
  })();

  const districtNames = districts.map((d) => d.name);

  const handleRegionChange = useCallback((val: string) => {
    setRegion(val);
    setCity('');
    setDistrict('');
    setDistricts([]);
  }, []);

  const handleCityChange = useCallback((val: string) => {
    setCity(val);
    setDistrict('');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep('submitting');
    setErrorMsg('');
    try {
      await customRequestsApi.create({
        fullName, email,
        phone: phone || undefined,
        intent,
        region: region || undefined,
        city: city || undefined,
        district: district || undefined,
        address: address || undefined,
        message,
      });
      setStep('success');
    } catch (err: any) {
      const msg = err?.message ?? 'Une erreur est survenue. Veuillez réessayer.';
      setErrorMsg(Array.isArray(msg) ? msg.join(' ') : msg);
      setStep('error');
    }
  };

  if (step === 'success') {
    return (
      <div className="flex flex-col items-center gap-5 py-14 text-center">
        <div className="h-20 w-20 rounded-full bg-success/10 flex items-center justify-center ring-8 ring-success/5">
          <CheckCircle2 className="h-9 w-9 text-success" />
        </div>
        <div>
          <p className="font-display text-h3 text-ink">Demande envoyée !</p>
          <p className="text-sm text-ink-secondary mt-2 max-w-sm leading-relaxed">
            Merci, <span className="font-semibold">{fullName}</span>. Notre équipe
            analysera votre demande et vous contactera dans les 24&nbsp;h.
          </p>
        </div>
        <button
          onClick={() => {
            setStep('idle');
            setFullName(''); setEmail(''); setPhone('');
            setRegion(''); setCity(''); setDistrict(''); setAddress(''); setMessage('');
            setIntent('buy');
          }}
          className="text-sm text-accent underline underline-offset-2 mt-2"
        >
          Soumettre une autre demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* Intent toggle */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-ink">
          Je souhaite <span className="text-error">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'buy', label: 'Acheter un bien' },
            { value: 'rent', label: 'Louer un bien' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setIntent(value as 'buy' | 'rent')}
              className={cn(
                'py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all',
                intent === value
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-border text-ink hover:border-accent/40',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contact info */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-ink">
            Nom complet <span className="text-error">*</span>
          </label>
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Prénom et nom"
            required
            autoComplete="name"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-ink">
            E-mail <span className="text-error">*</span>
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            required
            autoComplete="email"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-ink">Téléphone</label>
        <Input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+212 6XX XX XX XX"
          autoComplete="tel"
        />
      </div>

      {/* Location */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Autocomplete
          label="Région"
          placeholder="Ex : Marrakech-Safi…"
          value={region}
          onChange={handleRegionChange}
          options={regionNames}
        />
        <Autocomplete
          label="Ville"
          placeholder="Ex : Marrakech…"
          value={city}
          onChange={handleCityChange}
          options={cityNames}
          disabled={cityNames.length === 0}
        />
        <Autocomplete
          label="Quartier"
          placeholder="Ex : Guéliz, Médina…"
          value={district}
          onChange={setDistrict}
          options={districtNames}
          disabled={!city}
        />
      </div>

      {/* Address */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-ink">Adresse</label>
        <Input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Ex : 12, Rue Mohammed V…"
          autoComplete="street-address"
        />
      </div>

      {/* Message */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-ink">
          Décrivez votre projet <span className="text-error">*</span>
        </label>
        <Textarea
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type de bien recherché, surface approximative, budget, délai souhaité, critères importants…"
          required
          minLength={20}
          maxLength={2000}
        />
        <p className="text-xs text-ink-muted text-right">{message.length}/2000</p>
      </div>

      {/* Error */}
      {step === 'error' && (
        <div className="flex items-start gap-3 p-4 bg-error/5 border border-error/20 rounded-xl text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      <Button type="submit" size="lg" className="w-full" disabled={step === 'submitting'}>
        {step === 'submitting' ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi en cours…</>
        ) : (
          <><Send className="h-4 w-4 mr-2" />Envoyer ma demande</>
        )}
      </Button>

      <p className="text-xs text-ink-muted text-center">
        En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe.
        Vos données sont traitées conformément à notre{' '}
        <a href="/politique-confidentialite" className="underline hover:text-ink">
          politique de confidentialité
        </a>.
      </p>
    </form>
  );
}
